import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { McTopBar } from "@maersk-global/mds-react-wrapper/components-core/mc-top-bar";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core/mc-table";
import { McToast } from "@maersk-global/mds-react-wrapper/components-core/mc-toast";
import { McNotification } from "@maersk-global/mds-react-wrapper/components-core/mc-notification";
import { McFileUpload } from "@maersk-global/mds-react-wrapper/components-core/mc-file-upload";
import { AL } from "../../al";
import { jwtDecode } from "jwt-decode";
import "./Preview.css";
const Preview = () => {
    const { sessionId } = useParams();
    const [searchParams] = useSearchParams();
    const filename = searchParams.get('filename') || 'Unknown File';
    const navigate = useNavigate();


    const [previewData, setPreviewData] = useState({});
    const [loading, setLoading] = useState(false);
    const [toastVisible, setToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastAppearance, setToastAppearance] = useState("info");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [groupByFO, setGroupByFO] = useState(true);

    // Add ZIP upload and submit functionality
    const [zipFiles, setZipFiles] = useState(null);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [poLocations, setPoLocations] = useState({});
    const zipFilesRef = useRef(null);
    const [uniqueID, setUniqueID] = useState("")
    const [failedLocations, setFailedLocations] = useState({});


    useEffect(() => {
        // Example: get token from localStorage or API
        const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
        if (token) {
            try {
                const decoded = jwtDecode(token); // decode JWT
                setUniqueID(decoded.uniqueId);

            } catch (err) {
                console.error("Invalid token", err);
            }
        }
    }, []);

    const columnHeaderMap = {
        fo: "FO Number",
        text: "Charge Description",
        mrs_non_mrs: "MRS/Non-MRS",
        lane_id: "Lane ID",
        currency: "Currency",
        origin_charge_destination_charge: "Origin/Destination",
        maersk_code: "Maersk Code",
        material_name: "Material Name",
        material_description: "Material Description",
        material_name_in_mrs: "Material in MRS",
        vestas_code: "Vestas Code",
        vestas_name: "Vestas Name",
        uom: "UOM",
        total_days: "Total Days",
        qty: "Quantity",
        unit_price: "Unit Price",
        amount: "Amount",
        plant: "Plant",
        invoice_team: "Invoice Team",
        etd: "ETD",
        eta: "ETA",
        original_port_of_loading_city_name: "Port of Loading",
        port_of_discharge_city_name: "Port of Discharge",
        place_of_delivery_city_name: "Place of Delivery",
        place_of_receipt_city_name: "Place of Receipt",
        d_d_storage_start_date: "D&D Storage Start Date",
        d_d_storage_end_date: "D&D Storage End Date",
    };

    // Helper function to format column headers not in the map
    const formatColumnHeader = (key) => {
        if (columnHeaderMap[key]) {
            return columnHeaderMap[key];
        }
        // Auto-format: replace underscores with spaces and capitalize each word
        const upperCaseWords = ['id', 'uom', 'fo', 'mrs', 'etd', 'eta', 'po', 'd'];
        return key
            .split('_')
            .map(word => {
                if (upperCaseWords.includes(word.toLowerCase())) {
                    return word.toUpperCase();
                }
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
            })
            .join(' ');
    };

    const isMissingValue = (value) =>
        value === null || value === undefined || value === "" || value === "-";

    const hasMissingApprovalFields = (record) =>
        isMissingValue(record.revised_approver) ||
        isMissingValue(record.cost_approver) ||
        isMissingValue(record.po_creator) ||
        isMissingValue(record.invoice_creator);

    const showToast = (message, appearance = "info") => {
        setToastMessage(message);
        setToastAppearance(appearance);
        setToastVisible(true);
        setTimeout(() => setToastVisible(false), 4000);
    };

    const handleLogout = () => {
        AL.logoutRedirect({
            post_logout_uri: window.location.origin + "/"
        });
    };


    const handleGoBack = () => {
        navigate('/requestCharge');
    };
    const performSubmit = async () => {
        setLoadingSubmit(true);
        const accessToken = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);

        try {
            const allData = Object.values(previewData).flat();
            const zeroAmountRecords = allData.filter(record =>
                record.amount === 0 ||
                record.amount === "0" ||
                parseFloat(record.amount) === 0
            );

            if (zeroAmountRecords.length > 0) {
                showToast(`Error: ${zeroAmountRecords.length} record(s) have zero amount.`, "error");
                setLoadingSubmit(false);
                return;
            }

            // 1️⃣ Upload ZIP files if any
            if (zipFilesRef.current && zipFilesRef.current.length > 0) {
                const zipFormData = new FormData();
                zipFormData.append("sessionId", sessionId);

                Array.from(zipFilesRef.current).forEach((file) => {
                    zipFormData.append("files", file);
                });

                await axios.post(
                    `${process.env.REACT_APP_API_BASE_URL}/api/upload-zips/`,
                    zipFormData,
                    {
                        headers: {
                            "Authorization": `Bearer ${accessToken}`,
                            "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY,
                        },
                    }
                );

                showToast(`Uploaded ${zipFilesRef.current.length} ZIP file(s)`, "info");
            } else {
                const proceed = window.confirm("No files have been uploaded. Do you want to continue?");
                if (!proceed) return;
            }

            // 2️⃣ Send mail
            const res = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/send-mail/`,
                {
                    session_id: sessionId,
                    source: uniqueID,
                },
                {
                    headers: {
                        "Authorization": `Bearer ${accessToken}`,
                        "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY,
                    },
                }
            );

            const sent = res.data.sent || [];
            const failed = res.data.failed || {};
            const salesforce = res.data.salesforce || {};

            // ✅ Show success toast for sent items
            if (sent.length > 0) {
                showToast(`Sent: ${sent.join(", ")}`, "info");
            }

            // ⚠️ Show failure toast for each failed item
            if (Object.keys(failed).length > 0) {
                Object.entries(failed).forEach(([key, message]) => {
                    showToast(`Failed for ${key}: ${message}`, "error");
                });
            }

            // 3️⃣ Extract locations for each PO safely
            const formattedLocations = {};
            const formattedFailures = {}; // <-- ADDED

            Object.entries(salesforce).forEach(([po, value]) => {
                // ❌ FAILED CASE
                if (value?.error) {
                    formattedFailures[po] = value.error;
                    formattedLocations[po] = [];
                    return;
                }

                // ✅ SUCCESS CASE
                if (Array.isArray(value)) {
                    formattedLocations[po] = value.flatMap((locObj) => {
                        const responses = locObj?.body?.compositeResponse || [];
                        if (Array.isArray(responses)) {
                            return responses.map((resp) => resp?.httpHeaders?.Location || "N/A");
                        }
                        return [];
                    });
                } else {
                    formattedLocations[po] = [];
                }
            });

            // 🔥 Save success & failed states
            setPoLocations(formattedLocations);
            setFailedLocations(formattedFailures); // <-- ADDED

        } catch (err) {
            console.error("Submit error:", err);

            // Try to extract meaningful messages if the error is from Axios
            if (err.response?.data) {
                const data = err.response.data;
                if (data.failed) {
                    Object.entries(data.failed).forEach(([key, message]) => {
                        showToast(`Failed for ${key}: ${message}`, "error");
                    });
                } else if (data.message) {
                    showToast(data.message, "error");
                } else {
                    showToast("Error occurred while bulk uploading", "error");
                }
            } else {
                showToast("Error occurred while bulk uploading", "error");
            }
        } finally {
            setLoadingSubmit(false);
        }
    };


    useEffect(() => {
        const fetchPreviewData = async () => {
            if (!sessionId) return;
            const accessToken = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
            setLoading(true);
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_BASE_URL}/api/preview?session_id=${sessionId}`, {
                    headers: {
                        "Authorization": `Bearer ${accessToken}`,
                        "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY
                    },
                }
                );

                // Process the data from the API response
                const apiData = response.data.data || {};
                const processedData = {};

                Object.entries(apiData).forEach(([foKey, foDataArray]) => {
                    if (Array.isArray(foDataArray)) {
                        processedData[foKey] = foDataArray.map((item, index) => ({
                            ...item,
                            id: `${foKey}_${index}`
                        }));
                    }
                });

                setPreviewData(processedData);
            } catch (error) {
                console.error('Error fetching preview data:', error);
                showToast("Failed to load preview data", "error");
            } finally {
                setLoading(false);
            }
        };

        fetchPreviewData();
    }, [sessionId]);

    if (loading) {
        return (
            <div className="mds">
                <McTopBar
                    product="Maersk | Unplanned Charges"
                     productshort="Maersk"
                    style={{
                        paddingLeft: "20px",
                        height: "64px",
                        paddingRight: "20px",
                        marginTop: "-5px",
                        borderBottom: "1px solid #DBDBDB",
                    }}
                >
                    <div slot="actions" style={{ display: "flex", alignItems: "center", color: "white" }}>
                        <McButton
                            onClick={handleGoBack}
                            label="Back to Request"
                            appearance="primary"
                            fit="small"
                            style={{ marginRight: "1rem" }}
                        />
                        <McButton onClick={handleLogout} label="Sign Out" appearance="secondary" fit="small" />
                    </div>
                </McTopBar>
                <main className="mds-page mds-container" style={{ padding: "2rem", minHeight: "95vh" }}>
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "50vh"
                    }}>
                        <span>Loading preview data...</span>
                    </div>
                </main>
            </div>
        );
    }

    const allPreviewRecords = Object.values(previewData).flat();
    const missingApprovalRecordCount = allPreviewRecords.filter(hasMissingApprovalFields).length;
    const submitDisabled = loadingSubmit || missingApprovalRecordCount > 0;

    return (
        <div className="mds">
            <McTopBar
                product="Maersk | Unplanned Charges"
                 productshort="Maersk"
                style={{
                    paddingLeft: "20px",
                    height: "64px",
                    paddingRight: "20px",
                    marginTop: "-5px",
                    borderBottom: "1px solid #DBDBDB",
                }}
            >
                <div slot="actions" style={{ display: "flex", alignItems: "center", color: "white" }}>
                    <McButton
                        onClick={handleGoBack}
                        label="Back to Request"
                        appearance="primary"
                        fit="small"
                        style={{ marginRight: "1rem" }}
                    />
                    <McButton onClick={handleLogout} label="Sign Out" appearance="secondary" fit="small" />
                </div>
            </McTopBar>

            <main className="mds-page mds-container" style={{ padding: "2rem", minHeight: "95vh" }}>
                <section style={{ marginBottom: "2rem" }}>
                    <h3 className="main-heading">Preview Data</h3>
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginTop: "1rem" }}>
                        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <input
                                type="checkbox"
                                checked={groupByFO}
                                onChange={(e) => {
                                    setGroupByFO(e.target.checked);
                                    setPage(1);
                                }}
                            />
                            Group by FO
                        </label>
                    </div>
                </section>

                {Object.keys(previewData).length === 0 ? (
                    <div style={{
                        padding: "2rem",
                        textAlign: "center",
                        color: "#666",
                        border: "1px dashed #ddd",
                        borderRadius: "8px",
                        backgroundColor: "#f9f9f9"
                    }}>
                        <span>No preview data available for this session</span>
                    </div>
                ) : (
                    <>
                        <div style={{ display: "flex", flexDirection: "column", marginBottom: "2rem" }}>
                            {/* Preview Data Tables */}
                            {(() => {
                                if (groupByFO) {
                                    // Group data by FO
                                    const foKeys = Object.keys(previewData);
                                    const totalFOPages = Math.ceil(foKeys.length / pageSize);
                                    const startIdx = (page - 1) * pageSize;
                                    const endIdx = startIdx + pageSize;
                                    const paginatedFOs = foKeys.slice(startIdx, endIdx);

                                    return (
                                        <>
                                            <div style={{
                                                overflowY: "auto",
                                                maxHeight: "70vh",
                                                minHeight: "300px"
                                            }}>
                                                {paginatedFOs.map((foKey) => (
                                                    <div key={foKey} style={{
                                                        marginBottom: "2rem",
                                                        border: previewData[foKey].length > 3 ? "1px solid #ddd" : "none",
                                                        borderRadius: "8px",
                                                        padding: "1rem"
                                                    }}>
                                                        <h4 style={{
                                                            backgroundColor: "#f0f4f8",
                                                            padding: "0.5rem",
                                                            margin: "-1rem -1rem 1rem -1rem",
                                                            borderRadius: "8px 8px 0 0",
                                                            fontWeight: "600",
                                                            fontSize: "1.1rem"
                                                        }}>
                                                            FO: {foKey} ({previewData[foKey].length} records)
                                                        </h4>
                                                       
                                                            <McTable
                                                                data={previewData[foKey]}
                                                                columns={Object.keys(previewData[foKey][0] || {}).map((key) => ({
                                                                    id: key,
                                                                    label: formatColumnHeader(key),
                                                                    noWrap: true,
                                                                    cellStyle: key === "fo" ? { backgroundColor: "#f0f4f8", fontWeight: 600 } : {},
                                                                }))}
                                                                datakey="id"
                                                                height="auto"
                                                                recordsperpage={previewData[foKey].length}
                                                                selected={[]}
                                                                hidecolumns={[]}
                                                                striped={true}
                                                                stickyHeader={previewData[foKey].length > 10}
                                                                style={{
                                                                    border: "none",
                                                                    borderRadius: "4px",
                                                                    minHeight: "200px"
                                                                }}
                                                            />
                                                        </div>
                                                    
                                                ))}
                                            </div>

                                            {/* Pagination for FO groups */}
                                            <div style={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                gap: "1rem",
                                                padding: "1rem",
                                                borderTop: "1px solid #eee",
                                                backgroundColor: "#f9f9f9",
                                                marginTop: "1rem"
                                            }}>
                                                <McButton
                                                    onClick={() => setPage(prev => Math.max(1, prev - 1))}
                                                    disabled={page === 1}
                                                    appearance="secondary"
                                                    fit="small"
                                                >
                                                    Previous
                                                </McButton>
                                                <span>
                                                    Page {page} of {totalFOPages}
                                                    ({foKeys.length} FO groups total)
                                                </span>
                                                <McButton
                                                    onClick={() => setPage(prev => prev + 1)}
                                                    disabled={page >= totalFOPages}
                                                    appearance="secondary"
                                                    fit="small"
                                                >
                                                    Next
                                                </McButton>
                                            </div>
                                        </>
                                    );
                                } else {
                                    // Flat view with pagination
                                    const allData = Object.values(previewData).flat();
                                    const totalPages = Math.ceil(allData.length / pageSize);
                                    const startIdx = (page - 1) * pageSize;
                                    const endIdx = startIdx + pageSize;
                                    const paginatedData = allData.slice(startIdx, endIdx);

                                    return (
                                        <>
                                            <div style={{
                                                    borderRadius: "8px",
                                                    overflow: "scroll",
                                                    height: "60vh",
                                                    display: "flex",
                flexDirection: "column"
              }}>
                                                <McTable
                                                    data={paginatedData}
                                                    disableoverflow
                                                    columns={Object.keys(allData[0] || {}).map((key) => ({
                                                        id: key,
                                                        label: formatColumnHeader(key),
                                                        noWrap: true,
                                                        cellStyle: key === "fo" ? { backgroundColor: "#f0f4f8", fontWeight: 600 } : {},
                                                    }))}
                                                    datakey="id"
                                                    height="auto"
                                                    recordsperpage={paginatedData.length || pageSize}
                                                    striped={true}
                                                    stickyHeader={paginatedData.length > 15}
                                                    style={{
                                                        border: "none",
                                                        minHeight: "100%"
                                                    }}
                                                />
                                            </div>

                                            {/* Pagination for flat view */}
                                            <div style={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                gap: "1rem",
                                                padding: "1rem",
                                                borderTop: "1px solid #eee",
                                                backgroundColor: "#f9f9f9",
                                                marginTop: "1rem"
                                            }}>
                                                <McButton
                                                    onClick={() => setPage(prev => Math.max(1, prev - 1))}
                                                    disabled={page === 1}
                                                    appearance="secondary"
                                                    fit="small"
                                                >
                                                    Previous
                                                </McButton>

                                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                    <span>Page {page} of {totalPages}</span>
                                                    <select
                                                        value={pageSize}
                                                        onChange={(e) => {
                                                            setPageSize(Number(e.target.value));
                                                            setPage(1);
                                                        }}
                                                        style={{
                                                            padding: "4px 8px",
                                                            borderRadius: "4px",
                                                            border: "1px solid #ccc"
                                                        }}
                                                    >
                                                        <option value={10}>10 per page</option>
                                                        <option value={20}>20 per page</option>
                                                        <option value={30}>30 per page</option>
                                                        <option value={40}>40 per page</option>
                                                    </select>
                                                </div>

                                                <span>({allData.length} records total)</span>

                                                <McButton
                                                    onClick={() => setPage(prev => prev + 1)}
                                                    disabled={page >= totalPages}
                                                    appearance="secondary"
                                                    fit="small"
                                                >
                                                    Next
                                                </McButton>
                                            </div>
                                        </>
                                    );
                                }
                            })()}
                        </div>

                        {/* Upload and Submit Section */}
                        <section style={{
                            backgroundColor: "white",
                            padding: "2rem",
                            borderRadius: "8px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            border: "1px solid #e9ecef",
                            marginBottom: "2rem"
                        }}>
                            <div style={{ marginBottom: "2rem" }}>
                                <h3 className="main-heading" style={{
                                    fontSize: "1.2rem",
                                    fontWeight: "600",
                                    color: "#1f2937",
                                    marginBottom: "1rem",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem"
                                }}>

                                    Upload Files
                                </h3>
                                <div style={{
                                    padding: "1.5rem",
                                    backgroundColor: "#f8f9fa",
                                    borderRadius: "6px",
                                    border: "2px dashed #cbd5e1"
                                }}>
                                    <McFileUpload
                                        accept=".zip,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.jpg,.png,.msg,.eml"
                                        multiple
                                        input={(e) => {
                                            zipFilesRef.current = e?.detail || null;
                                            setZipFiles(e?.detail || null);
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Submit Button - Right Aligned */}
                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                gap: "0.5rem",
                                paddingTop: "1rem",
                                borderTop: "1px solid #e5e7eb"
                            }}>
                                {missingApprovalRecordCount > 0 && (
                                    <span style={{ color: "#b91c1c", fontSize: "0.9rem" }}>
                                        {missingApprovalRecordCount} record(s) are missing Revised Approver (Event Approver), Cost Approver, PO Creator, or Invoice Creator. Resolve these before submitting.
                                    </span>
                                )}
                                <McButton
                                    onClick={performSubmit}
                                    appearance="primary"
                                    disabled={submitDisabled}
                                    style={{
                                        fontSize: "1rem",
                                        fontWeight: "600"
                                    }}
                                >
                                    {loadingSubmit ? "Submitting..." : "Send to Salesforce"}
                                </McButton>
                            </div>
                        </section>

                        {/* PO Locations Section */}
                        {Object.keys(poLocations).length > 0 && (
                            <section style={{
                                backgroundColor: "white",
                                padding: "2rem",
                                borderRadius: "8px",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                border: "1px solid #e9ecef"
                            }}>
                                <h3 style={{
                                    fontSize: "1.2rem",
                                    fontWeight: "600",
                                    color: "#1f2937",
                                    marginBottom: "1rem",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem"
                                }}>
                                    Submission Results
                                </h3>
                                <p style={{
                                    color: "#6b7280",
                                    marginBottom: "1.5rem",
                                    fontSize: "1rem",
                                    lineHeight: "1.5"
                                }}>
                                    The following Purchase Orders have been successfully submitted. Click "View" to see or modify the charge details.
                                </p>

                                <div style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                                    gap: "1rem"
                                }}>
                                    {Object.entries(poLocations).map(([po, paths]) => {
                                        // Only keep paths that belong to charge referenceIds
                                        const uniquePaths = Array.from(new Set(paths)).filter((path) =>
                                            path.includes("Charge__c")
                                        );

                                        if (uniquePaths.length === 0) return null;

                                        return (
                                            <div key={po} style={{
                                                border: "1px solid #e5e7eb",
                                                borderRadius: "8px",
                                                padding: "1.5rem",
                                                backgroundColor: "#f9fafb"
                                            }}>
                                                <div style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    marginBottom: "1rem"
                                                }}>
                                                    <h4 style={{
                                                        fontSize: "1.1rem",
                                                        fontWeight: "600",
                                                        color: "#1f2937",
                                                        margin: "0"
                                                    }}>
                                                        FO: {po}
                                                    </h4>
                                                    <span style={{
                                                        backgroundColor: "#10b981",
                                                        color: "white",
                                                        padding: "0.25rem 0.75rem",
                                                        borderRadius: "12px",
                                                        fontSize: "0.75rem",
                                                        fontWeight: "500"
                                                    }}>
                                                        Success
                                                    </span>
                                                </div>
                                                <ul style={{
                                                    listStyle: "none",
                                                    padding: "0",
                                                    margin: "0",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: "0.5rem"
                                                }}>
                                                    {uniquePaths.map((path, idx) => {
                                                        const salesforceId = path.split("/").pop();
                                                        const url = `https://maersk.lightning.force.com/lightning/r/Charge__c/${salesforceId}/view`;

                                                        return (
                                                            <li key={idx} style={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                                padding: "0.5rem 0",
                                                                borderBottom: idx < uniquePaths.length - 1 ? "1px solid #e5e7eb" : "none"
                                                            }}>
                                                                <span style={{
                                                                    color: "#374151",
                                                                    fontSize: "0.875rem"
                                                                }}>
                                                                    Charge ID {idx + 1}
                                                                </span>
                                                                <a
                                                                    href={url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    style={{
                                                                        color: "#3b82f6",
                                                                        textDecoration: "none",
                                                                        fontSize: "0.875rem",
                                                                        fontWeight: "500",
                                                                        padding: "0.25rem 0.5rem",
                                                                        borderRadius: "4px",
                                                                        border: "1px solid #3b82f6",
                                                                        transition: "all 0.2s"
                                                                    }}
                                                                    onMouseOver={(e) => {
                                                                        e.target.style.backgroundColor = "#3b82f6";
                                                                        e.target.style.color = "white";
                                                                    }}
                                                                    onMouseOut={(e) => {
                                                                        e.target.style.backgroundColor = "transparent";
                                                                        e.target.style.color = "#3b82f6";
                                                                    }}
                                                                >
                                                                    View
                                                                </a>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        )}
                        {/* FAILED SECTION */}
                        {Object.keys(failedLocations).length > 0 && (
                            <section style={{
                                backgroundColor: "white",
                                padding: "2rem",
                                borderRadius: "8px",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                border: "1px solid #e9ecef",
                                marginTop: "2rem"
                            }}>
                                <h3 style={{
                                    fontSize: "1.2rem",
                                    fontWeight: "600",
                                    color: "#b91c1c",
                                    marginBottom: "1rem"
                                }}>
                                    Failed Submissions
                                </h3>

                                <p style={{ color: "#6b7280", marginBottom: "1.5rem" }}>
                                    These Purchase Orders could not be submitted due to Salesforce validation errors.
                                </p>

                                <div style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                                    gap: "1rem"
                                }}>
                                    {Object.entries(failedLocations).map(([po, errorMsg]) => (
                                        <div key={po} style={{
                                            border: "1px solid #fecaca",
                                            borderRadius: "8px",
                                            padding: "1.5rem",
                                            backgroundColor: "#fef2f2"
                                        }}>
                                            <div style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                marginBottom: "1rem"
                                            }}>
                                                <h4 style={{
                                                    fontSize: "1.1rem",
                                                    fontWeight: "600",
                                                    color: "#991b1b",
                                                    margin: 0
                                                }}>
                                                    FO: {po}
                                                </h4>
                                                <span style={{
                                                    backgroundColor: "#dc2626",
                                                    color: "white",
                                                    padding: "0.25rem 0.75rem",
                                                    borderRadius: "12px",
                                                    fontSize: "0.75rem"
                                                }}>
                                                    Failed
                                                </span>
                                            </div>

                                            <div style={{ color: "#7f1d1d", fontSize: "0.9rem" }}>
                                                {errorMsg}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                    </>
                )}
            </main>

            <McToast appearance={toastAppearance} open={toastVisible} close={() => setToastVisible(false)}>
                <McNotification body={toastMessage} />
            </McToast>
        </div>
    );
};

export default Preview;
